import classnames from 'classnames';
import {includes} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {Draggable} from '@hello-pangea/dnd';
import {Link} from 'react-router-dom';
import EarningText from 'shared/college-earnings/components/earning-text';
import NotEarningText, {
  NOT_EARNING_STATUS_COMPONENTS,
} from 'shared/college-earnings/components/not-earning-text';
import {EARNINGS_PROP_TYPES, shortCollegeName} from 'shared/college-earnings/utils';
import Icon from 'shared/components/icon';
import {getMedia, watchMedia} from 'shared/mq';
import {EARNING_STATUSES} from 'shared/utils';
import AdmissionLikelihoodTag from './admission-likelihood-tag';

class Earning extends React.Component {
  static propTypes = {
    earnings: PropTypes.shape(EARNINGS_PROP_TYPES).isRequired,
    previousEarnings: PropTypes.shape({
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      amount: PropTypes.number,
      earningRelationship: PropTypes.shape({
        statusType: PropTypes.string.isRequired,
      }).isRequired,
    }),
    lastChild: PropTypes.bool.isRequired,
    firstChild: PropTypes.bool.isRequired,
    animating: PropTypes.bool,
    showNewEarnings: PropTypes.bool,
    showBorderBottom: PropTypes.bool,
    // Index used for drag and drop
    index: PropTypes.number,
    showBuildYourListOnTrack: PropTypes.bool.isRequired,
    disableDrag: PropTypes.bool,
  };

  state = {
    active: false,
  };

  componentDidMount() {
    this.mq = watchMedia(() => {
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.mq.remove();
  }

  getClassName = (difference) => {
    const {animating, showNewEarnings} = this.props;

    let className = 'college-earnings-earning';

    if (this.state.active) {
      className += ' active';
    }
    if (animating && showNewEarnings && difference !== 0) {
      if (difference > 0) {
        className += ' increased';
      } else {
        className += ' decreased';
      }
    }

    return className;
  };

  setActiveClass =
    (activeSetting, mediaQuery = true) =>
    () => {
      mediaQuery && this.setState({active: activeSetting});
    };

  render() {
    const mq = getMedia();

    const {
      earnings: {
        _id,
        name,
        nickname,
        slug,
        earningRelationship,
        earningRelationship: {statusType},
        amount,
        divisor,
        admissionLikelihoodScoreCategory,
        maxEarnings,
        useApplyByTerminology,
      },
      previousEarnings,
      showBorderBottom,
      showNewEarnings,
      animating,
      index,
      disableDrag,
      showBuildYourListOnTrack,
    } = this.props;

    const prevEarning = previousEarnings ? previousEarnings.amount : 0;
    const difference = (amount || 0) - prevEarning;
    const maxStudentEarningsReached = !!maxEarnings && amount >= maxEarnings;

    const showPerYearEarnings =
      !admissionLikelihoodScoreCategory && !NOT_EARNING_STATUS_COMPONENTS[statusType];
    const display = (
      <>
        <Link
          to={`/edu/${slug}`}
          className={this.getClassName(difference)}
          onMouseDown={this.setActiveClass(true)}
          onMouseOver={this.setActiveClass(true, mq.MD_AND_UP)}
          onFocus={this.setActiveClass(true, mq.MD_AND_UP)}
          title={name}
          onMouseOut={this.setActiveClass()}
          onBlur={this.setActiveClass()}
        >
          <div
            className={`college-earnings-earning-container
          ${!showBorderBottom ? 'college-earnings-earning-container-no-bottom' : ''}`}
          >
            <div className="prefix-flex prefix-align-items">
              <img
                className="college-earnings-earning-image"
                alt={`${name} logo`}
                src={`//colleges-static.raise.me/${slug}/logo-120x120.png`}
              />
              <div className="prefix-flex-grow-1 college-earnings-earning-name">
                {shortCollegeName(name, nickname, includes(EARNING_STATUSES, statusType))}
              </div>
              <div className="college-statuses" data-testid="college-statuses">
                <NotEarningText
                  relationship={earningRelationship}
                  showTooltipFlag
                  useApplyByTerminology={useApplyByTerminology}
                />
                <EarningText
                  status={statusType}
                  earnings={amount}
                  earningsDivisor={divisor}
                  difference={difference}
                  previousEarnings={prevEarning}
                  animated={animating}
                  showNewEarnings={showNewEarnings}
                  showPerYearEarnings={showPerYearEarnings}
                  maxStudentEarningsReached={maxStudentEarningsReached}
                />
                {!!admissionLikelihoodScoreCategory && showBuildYourListOnTrack && (
                  <div className="college-earnings-normal-text">
                    <AdmissionLikelihoodTag
                      admissionLikelihoodScoreCategory={admissionLikelihoodScoreCategory}
                    />
                  </div>
                )}
              </div>
              <div className="college-earnings-handle">
                {!disableDrag && <Icon iconType="grip-v-r" testid="earning-drag-handle" />}
              </div>
            </div>
          </div>
        </Link>
      </>
    );

    const baseClassName = classnames('earning-and-menu-container', {
      'not-earning': !includes(EARNING_STATUSES, statusType),
    });
    return (
      <Draggable draggableId={_id} index={index} isDragDisabled={disableDrag}>
        {(provided, snapshot) => (
          <div
            className={classnames(baseClassName, {dragging: snapshot.isDragging})}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            {display}
          </div>
        )}
      </Draggable>
    );
  }
}

export default Earning;
